import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { backfillPnlService, getPnlMonthlySummaryService } from "../../../services/pnlReport.service";
import {
  getBackfillRange,
  getDefaultFilters,
  hasSelection,
  loadEntityList,
  readLastRefreshed,
  saveLastRefreshed,
} from "./pnlReportUtils";

const buildSummaryRequest = (filters) => ({
  year: filters.year,
  fromMonth: filters.fromMonth,
  toMonth: filters.toMonth,
  categories: filters.categories,
  subTypes: filters.subTypes,
});

// The backfill API takes a single category; with none or several selected it refreshes all of them.
const getBackfillCategory = (filters) => (filters.categories.length === 1 ? filters.categories[0] : null);

export const useSummaryData = (enabled) => {
  const [appliedFilters, setAppliedFilters] = useState(() => getDefaultFilters());
  const [rows, setRows] = useState([]);
  // Option lists come from the last unfiltered load so they stay complete while filtering.
  const [optionRows, setOptionRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const loadReport = useCallback(async (nextFilters) => {
    setAppliedFilters(nextFilters);
    setLoading(true);
    setLoadError("");
    const result = await loadEntityList(() => getPnlMonthlySummaryService(buildSummaryRequest(nextFilters)));
    setRows(result.rows);
    setLoadError(result.error);
    if (result.error) toast.error(result.error);
    else if (!hasSelection(nextFilters)) setOptionRows(result.rows);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (enabled) void loadReport(getDefaultFilters());
  }, [enabled, loadReport]);

  return { appliedFilters, rows, optionRows, loading, loadError, loadReport };
};

export const useRefreshFlow = ({ openMonths, appliedFilters, loadReport }) => {
  const [showRefresh, setShowRefresh] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(() => readLastRefreshed());

  const confirmRefresh = async () => {
    const range = getBackfillRange(openMonths);
    if (!range) return;
    setRefreshing(true);
    const { error } = await loadEntityList(() =>
      backfillPnlService({
        ...range,
        ignoreLockRule: false,
        category: getBackfillCategory(appliedFilters),
      })
    );
    if (error) {
      toast.error(error);
    } else {
      const refreshedAt = new Date().toISOString();
      saveLastRefreshed(refreshedAt);
      setLastRefreshed(refreshedAt);
      setShowRefresh(false);
      toast.success(`Report refreshed. ${openMonths.length} open month(s) recalculated.`);
      await loadReport(appliedFilters);
    }
    setRefreshing(false);
  };

  return { showRefresh, setShowRefresh, refreshing, lastRefreshed, confirmRefresh };
};
